import axios from 'axios';
import { apiClient } from './client';
import type { EffectiveEngageRole } from './org-users.api';
import type { ImpersonationKind } from '../stores/auth.store';

export interface AuthUserResponse {
  org_id: string;
  account_id: string;
  user_id: string;
  role: string;
  name: string;
  email: string;
}

interface ExchangeResponse {
  token: string;
  user: AuthUserResponse;
  is_super_admin: boolean;
  /** Effective Engage role at sign-in. Use useSession() for the live value. */
  engage_role: EffectiveEngageRole;
}

/**
 * Swap a JT token for an Engage session. Uses bare axios (not apiClient) so a
 * 401/403 here doesn't trigger the global session-ended redirects.
 *
 * 403 `{ error, code }` refusals: ENGAGE_ACCESS_NOT_GRANTED (JT admin hasn't
 * enabled Engage for this user) or ENGAGE_NOT_ENABLED_FOR_ORG (subscription).
 * 401 = bad JT token.
 */
export async function exchangeToken(jtToken: string): Promise<ExchangeResponse> {
  const { data } = await axios.post<{ data: ExchangeResponse }>(
    '/api/v1/auth/exchange',
    { jt_token: jtToken }
  );
  return data.data;
}

export interface JtTokenValidity {
  /** true = JT confirms the token; false = definitively expired/revoked;
   *  null = couldn't tell (transient) — don't take action yet. */
  valid: boolean | null;
  /** Present when valid is true. false = a JT admin switched off this user's
   *  Engage access. */
  engage_access?: boolean;
}

/**
 * Checks whether a JT token is still valid, and whether the user still has
 * Engage access. Returns:
 *   { valid: true, engage_access } — JT confirms the token is good
 *   { valid: false }               — JT returned 401/403 (expired/revoked)
 *   { valid: null }                — we couldn't reach JT or it returned a
 *                                    transient error; don't take action yet
 */
export async function validateJtToken(jtToken: string): Promise<JtTokenValidity> {
  try {
    const { data } = await axios.post<JtTokenValidity>('/api/v1/auth/jt-token-valid', {
      jt_token: jtToken,
    });
    return typeof data.engage_access === 'boolean'
      ? { valid: data.valid ?? null, engage_access: data.engage_access }
      : { valid: data.valid ?? null };
  } catch {
    // Network error reaching our own API — same treatment as a transient
    // upstream failure. Don't log the user out on a flaky network.
    return { valid: null };
  }
}

export interface SessionImpersonation {
  kind: ImpersonationKind;
  actor_user_id: string | null;
  actor_email: string | null;
  expires_at: string;
}

export interface SessionInfo {
  user: AuthUserResponse;
  engage_role: EffectiveEngageRole;
  engage_access: boolean | null;
  is_super_admin: boolean;
  impersonation: SessionImpersonation | null;
}

/** The current session as the server sees it. See hooks/useSession.ts. */
export async function getSession(): Promise<SessionInfo> {
  const { data } = await apiClient.get<{ data: SessionInfo }>('/auth/session');
  return data.data;
}
